import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Shield, User, Lock, Mail, Phone, Key, AlertCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { authAPI } from '../services/api';
import { getErrorMessage } from '../utils/errorHandler';

const RegisterAdmin = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    companyCode: '',
    userName: '',
    userEmail: '',
    userPhone: '',
    password: '',
    confirmPassword: ''
  });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.companyCode || formData.companyCode.length < 6) {
      toast({ title: "Erreur", description: "Le code société est requis (minimum 6 caractères)", variant: "destructive" });
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast({ title: "Erreur", description: "Les mots de passe ne correspondent pas", variant: "destructive" });
      return;
    }

    if (formData.password.length < 8) {
      toast({ title: "Erreur", description: "Le mot de passe doit contenir au moins 8 caractères", variant: "destructive" });
      return;
    }

    setLoading(true);

    try {
      await authAPI.registerAdmin({ company_code: formData.companyCode, name: formData.userName, email: formData.userEmail, phone: formData.userPhone, password: formData.password });
      
      toast({ title: "✅ Compte créé", description: "Votre compte administrateur a été créé avec succès. Vous pouvez vous connecter." });
      
      setTimeout(() => { navigate('/login'); }, 2000);
      
    } catch (error) {
      toast({ title: "Erreur", description: getErrorMessage(error, "Impossible de créer le compte. Vérifiez le code société."), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <Shield className="w-12 h-12 text-purple-600 mr-2" />
            <h1 className="text-3xl font-bold text-gray-900">SECUTOR PRO</h1>
          </div>
          <p className="text-gray-600">Créer un compte administrateur</p>
        </div>

        <Card className="shadow-xl">
          <CardHeader>
            <CardTitle className="flex items-center text-2xl">
              <User className="w-6 h-6 mr-2 text-purple-600" />
              Compte Administrateur
            </CardTitle>
            <CardDescription>Rejoignez votre société avec le code fourni</CardDescription>
          </CardHeader>

          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              <div className="bg-purple-50 border-l-4 border-purple-600 p-4 rounded">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-purple-600 mr-2 mt-0.5" />
                  <div className="text-sm text-purple-900">
                    <p className="font-semibold mb-1">Code société requis</p>
                    <p>Demandez le code à l'administrateur principal de votre société.</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="companyCode" className="flex items-center">
                  <Key className="w-4 h-4 mr-2 text-purple-600" />
                  Code Société *
                </Label>
                <Input id="companyCode" name="companyCode" type="text" placeholder="Ex: SEC-2024-ABC123" value={formData.companyCode} onChange={handleInputChange} required className="text-lg" />
                <small className="text-gray-500">Code fourni par votre société</small>
              </div>

              <div className="border-t pt-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Vos informations</h3>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="userName">Nom complet *</Label>
                    <Input id="userName" name="userName" type="text" placeholder="Jean Dupont" value={formData.userName} onChange={handleInputChange} required />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="userEmail">Email *</Label>
                    <Input id="userEmail" name="userEmail" type="email" placeholder="jean.dupont@email.com" value={formData.userEmail} onChange={handleInputChange} required />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="userPhone">Téléphone</Label>
                    <Input id="userPhone" name="userPhone" type="tel" placeholder="+33 6 12 34 56 78" value={formData.userPhone} onChange={handleInputChange} />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password">Mot de passe *</Label>
                    <Input id="password" name="password" type="password" placeholder="Minimum 8 caractères" value={formData.password} onChange={handleInputChange} required />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirmer le mot de passe *</Label>
                    <Input id="confirmPassword" name="confirmPassword" type="password" placeholder="Retapez votre mot de passe" value={formData.confirmPassword} onChange={handleInputChange} required />
                  </div>
                </div>
              </div>
            </CardContent>

            <CardFooter className="flex flex-col space-y-4">
              <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700" disabled={loading}>
                {loading ? 'Création en cours...' : 'Créer mon compte'}
              </Button>

              <div className="text-center text-sm text-gray-600">
                <p className="mb-2">Vous êtes l'administrateur principal ?</p>
                <Link to="/register-company" className="text-purple-600 hover:underline font-semibold">
                  Inscrire votre société →
                </Link>
              </div>

              <div className="text-center text-sm">
                <span className="text-gray-600">Déjà un compte ? </span>
                <Link to="/login" className="text-purple-600 hover:underline font-semibold">Se connecter</Link>
              </div>
            </CardFooter>
          </form>
        </Card>

        <div className="mt-6 text-center text-sm text-gray-500">
          <p>© 2024 SECUTOR PRO LLC - info@hts-investigation.fr</p>
        </div>
      </div>
    </div>
  );
};

export default RegisterAdmin;